import type {Middleware, BareFetcher, SWRHook, SWRResponse} from 'swr';
import type {z} from 'zod';

/**
 * Middleware to integrate Zod schema validation with SWR.
 *
 * This middleware allows for automatic parsing and validation of SWR responses
 * using a Zod schema. It adds a `parsed` property to the SWR response which contains
 * the validated and parsed data.
 *
 * @example
 * // Define a Zod schema
 * const schema = z.object({
 *   status: z.nativeEnum(IssueStatus),
 *   count: z.number(),
 * });
 *
 * // Use the middleware with SWR
 * const { data, error, parsed } = useApi(
 *   {
 *     endpoint: `publicListIssues`,
 *     entity: null,
 *     query: {
 *       aggregations,
 *     },
 *   },
 *   {
 *     aggregations,
 *   },
 *   {
 *     schema,
 *     use: [withZod],
 *   },
 * );
 *
 * // Accessing the parsed data
 * if (parsed) {
 *   console.log(parsed); // Data validated and parsed by the schema
 * }
 *
 * // Checking for errors
 * if (error) {
 *   console.error("Validation failed:", error);
 * }
 *
 * @typeParam T - The shape of the data as defined by the Zod schema.
 *
 * @returns The SWR response enhanced with Zod parsing. Adds a `parsed` property
 * if validation succeeds.
 */
export const withZod: Middleware =
  <T extends object>(useSWRNext: SWRHook) =>
  (key, fetcher, config) => {
    const swr = useSWRNext(key, fetcher as BareFetcher<any>, config);
    const schema = (config as any).schema as z.ZodSchema<T>;
    if (!schema || !swr.data) {
      return swr as SWRResponse & {parsed: never};
    }
    try {
      const data = schema.parse(swr.data);
      return {
        ...swr,
        parsed: data as T,
      };
    } catch (error) {
      return {
        ...swr,
        error: error ?? swr.error,
      };
    }
  };

/**
 * Type definition for the response when using the withZod middleware.
 *
 * @typeParam Response - The SWR response type.
 * @typeParam Schema - The Zod schema type.
 */
export type WithZod<Response extends SWRResponse = SWRResponse, Schema = z.ZodType> = Response & {parsed: Schema};
